import { zodResolver } from "@hookform/resolvers/zod"
import { Link, useNavigate } from "@tanstack/react-router"
import { CloudMoon } from "lucide-react"
import { useForm } from "react-hook-form"
import { useErrorDialog } from "@/shared/components/ErrorDialog"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form"
import { Input } from "@/shared/components/ui/input"
import { ApiError } from "@/shared/utils/apiError"
import { useRegisterMutation } from "./api/auth.mutations"
import { type RegisterInput, type RegisterValues, registerSchema } from "./auth.schemas"

export function RegisterPage() {
  const navigate = useNavigate()
  const { showApiError } = useErrorDialog()
  const register = useRegisterMutation()

  const form = useForm<RegisterInput, unknown, RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { username: "", password: "", confirmPassword: "" },
  })

  const onSubmit = (values: RegisterValues) => {
    register.mutate(values, {
      onSuccess: () => navigate({ to: "/" }),
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          form.setError("username", { message: "Användarnamnet är upptaget" })
          return
        }
        showApiError(err, { title: "Kunde inte skapa konto" })
      },
    })
  }

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <CloudMoon className="size-6 text-accent" />
            Slumra
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Användarnamn</FormLabel>
                    <FormControl>
                      <Input autoComplete="username" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lösenord</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bekräfta lösenord</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="mt-2 w-full" isLoading={register.isPending}>
                Skapa konto
              </Button>
            </form>
          </Form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Har du redan ett konto?{" "}
            <Link to="/login" className="text-accent underline-offset-4 hover:underline">
              Logga in
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  )
}
